import { AlertTriangle, Clock3, Download, FileArchive, Loader2, Trash2 } from "lucide-react";
import { EXPORT_ACTIVE, fileUrl, mb, phaseText } from "@/lib/exportAudioApi";

const STEMS_LABEL = { groups: "Lead · BGV · Music", all: "Every part", both: "Grouped + every part", none: "No stems" };

function Progress({ pack }) {
  const m = /^files:(\d+)\/(\d+)$/.exec(pack.phase || "");
  const pct = m ? Math.round((Number(m[1]) / Math.max(1, Number(m[2]))) * 100) : pack.phase === "zipping" ? 96 : 6;
  return (
    <div data-testid={`export-progress-${pack.id}`} className="mt-3">
      <p className="flex items-center gap-2 text-[13px] text-sky-100"><Loader2 className="h-4 w-4 animate-spin" /> {phaseText(pack)}…</p>
      <div className="mt-2 h-1.5 rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-sky-300 to-indigo-400 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Files({ pack }) {
  return (
    <div className="mt-3 space-y-2">
      <a href={fileUrl(pack.zip_url)} data-testid={`export-zip-${pack.id}`}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-300 py-2.5 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.01]">
        <FileArchive className="h-4 w-4" /> Download everything (.zip · {mb(pack.zip_bytes || 0)})
      </a>
      <ul className="divide-y divide-white/[0.06] rounded-2xl border border-white/[0.06] bg-black/25">
        {pack.files.map((f, i) => (
          <li key={f.path} className="flex items-center gap-3 px-3.5 py-2">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-medium">{f.path.split("/").pop()}</span>
              <span className="block truncate text-[10px] text-white/40">{f.path.includes("/") ? `${f.path.split("/")[0]} · ` : ""}{mb(f.bytes)}</span>
            </span>
            <a href={fileUrl(f.url)} data-testid={`export-file-${pack.id}-${i}`} aria-label={`Download ${f.label}`}
              className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg border border-white/10 text-white/60 transition-colors hover:bg-white/10 hover:text-white"><Download className="h-3.5 w-3.5" /></a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One export pack: what's in it, progress, then the zip and every single file. */
export default function ExportPackCard({ pack, current, onRemove }) {
  const active = EXPORT_ACTIVE.includes(pack.status);
  const what = [pack.mix?.label, pack.wav ? "WAV" : null, pack.mp3 ? "MP3" : null, STEMS_LABEL[pack.stems]].filter(Boolean).join(" · ");
  return (
    <li data-testid={`export-pack-${pack.id}`} data-status={pack.status} className={`rounded-2xl border p-4 ${current ? "border-sky-300/25 bg-sky-400/[0.04]" : "border-white/[0.07] bg-black/20"}`}>
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[14px] font-semibold">{pack.title}</span>
          <span className="block truncate text-[11px] text-white/45">{new Date(pack.created_at).toLocaleString()} · {what}</span>
        </span>
        <button type="button" data-testid={`export-remove-${pack.id}`} aria-label="Remove export" onClick={onRemove} disabled={active}
          className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-white/40 transition-colors hover:bg-rose-500/15 hover:text-rose-200 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
      </div>
      {active ? <Progress pack={pack} /> : null}
      {pack.status === "done" ? <Files pack={pack} /> : null}
      {pack.status === "error" ? <p data-testid={`export-error-${pack.id}`} className="mt-3 flex items-center gap-2 text-[13px] text-rose-200"><AlertTriangle className="h-4 w-4" /> {pack.error}</p> : null}
      {pack.status === "expired" ? <p data-testid={`export-expired-${pack.id}`} className="mt-3 flex items-center gap-2 text-[12px] text-white/45"><Clock3 className="h-4 w-4" /> Expired — make the export again to download it.</p> : null}
    </li>
  );
}
